// ---------------------------------------------------------------------------
// as-is-frame-flag — build-time gate for the As-Is markup frame render path
// (PR #2072, operator decision 2026-08-13).
//
// PR #2072 carries ~30 verified fixes plus one feature: an As-Is tab that
// renders a user's pasted legacy screen. That feature's sanitizer
// (`scanTagAttributes` / `neutralizeNavigationTargets`, as-is-markup.ts) has
// two open Critical defects — both the same vector. A sandboxed frame
// (`sandbox=""`, no `allow-scripts`/`allow-same-origin`) may always navigate
// ITSELF, and CSP does not govern navigation, so a `target` attribute the
// scanner fails to catch lets one click inside the frame replace the user's
// own pasted bytes with Studio's own response — or leave the appliance
// entirely via an absolute href. Operator decision: land the branch with
// this frame gated OFF so the ~30 unrelated fixes ship while the sanitizer
// is repaired separately.
//
// Scope: this flag governs ONLY the raw-markup iframe
// (`buildAsIsFrameDocument`'s only caller, as-is-view.tsx). The As-Is tab's
// pre-existing skeleton reading (derived `blocks`, rendered through AppV2 —
// already live on main before this branch) does not touch the sanitizer at
// all and is NOT gated by this flag; `buildAsIsSnapshotFromLegacyScreen`
// (capture) keeps writing `sourceMarkup` to `settings.asIsSnapshot`
// regardless of this flag, so data accumulates while only the render path
// stays dark.
//
// Shape mirrors `isLiveAboxEnabled` (design-system/ontology/abox-flag.ts) and
// `isKgBrowserEnabled` (design-system/kg-client.ts) — the project-established
// `import.meta.env.VITE_*` / default-OFF / `'1' | 'true'` case-insensitive
// truthy convention. No E2E_MOCK hard guard here (unlike abox-flag.ts): this
// gate guards a client-only rendering choice, not access to a live backend.
// ---------------------------------------------------------------------------

/**
 * Returns true when the As-Is markup frame (the sandboxed iframe rendering
 * the user's pasted-and-sanitized original) should mount. Default OFF — the
 * two open Critical sanitizer defects stay unreachable until this flips on.
 *
 * Truth table:
 *   VITE_AS_IS_FRAME unset                          -> false (default OFF, SHIPS)
 *   VITE_AS_IS_FRAME === '1' | 'true' (case-insens.) -> true
 *   anything else (e.g. 'yes', 'on', '0', 'false')   -> false
 */
export function isAsIsFrameEnabled(): boolean {
  const raw = import.meta.env.VITE_AS_IS_FRAME as string | undefined
  if (!raw) return false
  return raw === '1' || raw.toLowerCase() === 'true'
}
